import * as React from "react";
import { FC } from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { Link } from "react-router-dom";
import Markdown from "react-markdown";
import { MainLayoutContainer } from "components/Layout";
import { DragNDropFile } from "components/DragNDropFile";
import { CardOutline } from "components/Card";
import CustomTable from "components/Table/CustomTable";
import { FieldWithEntitySelector } from "components/FieldWithEntitySelector";
import { MarkdownStyleContainer } from "components/Container/MarkdownStyleContainer";
import { AdminRoutes } from "config/routes";
import { UPLOAD_FILE_MODAL } from "helpers/constants";
import { ReportAlert } from "../UploadCSVUsers/components";
import { useUploadCSVPrograms } from "./useUploadCSVPrograms";

export const UploadCSVPrograms: FC = () => {
  const {
    loading,
    completed,
    displayData,
    data,
    file,
    error,
    loadingData,
    report,
    organizations,
    selectedOrganization,
    selectedData,
    setSelectedOrganization,
    setDisplayData,
    setFile,
    handleContinue,
    handleDownloadClick,
    onSubmit,
    handleOnComplete,
  } = useUploadCSVPrograms();

  return (
    <MainLayoutContainer title="Upload CSV Programs" maxWidth={960}>
      <Box>
        {error && <Alert severity="error">{error}</Alert>}

        {!displayData && !loading && !completed && (
          <Box>
            <Box mt={2}>
              <DragNDropFile
                maxFileSizeMB={1}
                allowedExtensions={["csv"]}
                onFileUploaded={(uploadedFile) => setFile(uploadedFile)}
              />
            </Box>
            <Box mt={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography component={"div"} variant="h6">
                  CSV Format Required
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDownloadClick}
                  startIcon={<FileDownload />}
                  size="small"
                >
                  Download Template
                </Button>
              </Stack>
              <Box
                px={2}
                py={1}
                mt={1}
                sx={{
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.3)",
                  borderRadius: 1,
                }}
              >
                <MarkdownStyleContainer>
                  <Markdown>
                    {UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE_DESCRIPTION_PROGRAMS}
                  </Markdown>
                </MarkdownStyleContainer>
              </Box>
            </Box>
          </Box>
        )}

        {displayData && !loading && !completed && (
          <Box mt={2}>
            <CardOutline>
              <CardContent>
                <Typography variant="h6">Select Organization</Typography>
                <Box mt={2}>
                  <FieldWithEntitySelector
                    entityList={organizations}
                    element={selectedOrganization}
                    onChangeElement={(selected) => setSelectedOrganization(selected)}
                    entityName="Organizations"
                    displayField="name"
                    idField="org_id"
                  />
                </Box>
              </CardContent>
            </CardOutline>

            <CustomTable
              entities={data}
              columns={[
                { id: "name", label: "Name", value: "name" },
                { id: "start_date", label: "Start Date", value: "start_date" },
                { id: "end_date", label: "End Date", value: "end_date" },
                { id: "requirements", label: "Requirements", value: "requirements" },
              ]}
              handleActionClick={() => {}}
              fieldToFilter={"name"}
              isSelectable={true}
              extraActionItems={[]}
              idColumn={"row_key"}
              entitiesIdsAlreadySelected={selectedData.current}
              onSelectionChanged={(selected) => {
                selectedData.current = selected;
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
          {!displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button variant="outlined" component={Link} to={AdminRoutes.AcademicYearList}>
                  Cancel
                </Button>
              </Box>
              <Box mr={2}>
                <Button
                  disabled={!file}
                  onClick={() => handleContinue(file as File)}
                  variant="contained"
                  color="primary"
                >
                  Continue
                </Button>
              </Box>
            </>
          )}
          {displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button variant="outlined" onClick={() => setDisplayData(false)}>
                  Back
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  onClick={onSubmit}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
            </>
          )}
        </Box>

        {loading && (
          <Box mt={2} mb={4} maxWidth={720} sx={{ mx: "auto" }}>
            <Typography variant="body1" align="center">
              Uploading {~~((loadingData.current / loadingData.total) * 100)} %
            </Typography>
            <Box sx={{ width: "100%" }}>
              <LinearProgress
                variant="determinate"
                value={(loadingData.current / loadingData.total) * 100}
              />
            </Box>
          </Box>
        )}

        {completed && (
          <Box>
            <Stack spacing={2}>
              {report.success.length > 0 && (
                <ReportAlert
                  report={report.success}
                  severity="success"
                  textDescription="Programs imported successfully"
                />
              )}
              {report.alreadyExists.length > 0 && (
                <ReportAlert
                  report={report.alreadyExists}
                  severity="warning"
                  textDescription="Programs already existed"
                />
              )}
              {report.missingRequirements.length > 0 && (
                <ReportAlert
                  report={report.missingRequirements}
                  severity="warning"
                  textDescription="Missing requirements skipped"
                />
              )}
              {report.error.length > 0 && (
                <ReportAlert
                  report={report.error}
                  severity="error"
                  textDescription="Programs failed to import"
                />
              )}
            </Stack>
            <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Button
                disabled={loading}
                onClick={handleOnComplete}
                type="submit"
                variant="contained"
                color="primary"
              >
                Complete
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </MainLayoutContainer>
  );
};
